'use client';

import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Check,
  Loader2,
  RefreshCw,
  Sparkles,
  Users,
  X,
  Zap,
} from 'lucide-react';

import { useSmoothProgress } from '~/lib/use-smooth-progress';
import { useInvalidateCoinBalance } from '~/components/wallet';
import {
  type GenerationStatus,
  approveCharacterGeneration,
  cancelGeneration,
  checkGenerationStatus,
  rejectCharacterGeneration,
} from '~/lib/server/actions/character-generation-actions';

interface GenerationModalProps {
  isOpen: boolean;
  generationId: string | null;
  characterName: string;
  onClose: () => void;
  onRetry: () => void;
}

const MAX_IMAGE_RETRIES = 6;

/**
 * The final image URL points at an object uploaded to storage moments ago, so
 * the very first CDN request can transiently fail (cold cache). Polling stops
 * once status is `completed`, meaning a bare <img> that errors would stay
 * broken forever. Retry with cache-busting and a growing backoff instead.
 */
function GeneratedImage({ src, alt }: { src: string; alt: string }) {
  const [state, setState] = useState({
    attempt: 0,
    loaded: false,
    failed: false,
  });
  const { attempt, loaded, failed } = state;

  const url =
    attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}r=${attempt}`;

  const handleError = () => {
    if (attempt >= MAX_IMAGE_RETRIES) {
      setState((s) => ({ ...s, failed: true }));
      return;
    }

    window.setTimeout(
      () => {
        setState((s) =>
          s.failed ? s : { ...s, attempt: s.attempt + 1, loaded: false },
        );
      },
      600 * (attempt + 1),
    );
  };

  if (failed) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
        <AlertCircle className="h-8 w-8 text-orange-400" />
        <p className="text-xs text-gray-400">
          Your image is ready but taking a moment to load.
        </p>
        <button
          type="button"
          onClick={() =>
            setState({ attempt: attempt + 1, loaded: false, failed: false })
          }
          className="flex items-center gap-1.5 rounded-lg bg-gray-800 px-3 py-1.5 text-xs text-white transition-colors hover:bg-gray-700"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Reload image
        </button>
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
        </div>
      )}
      <img
        key={url}
        src={url}
        alt={alt}
        onLoad={() => setState((s) => ({ ...s, loaded: true }))}
        onError={handleError}
        className={`h-full w-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </>
  );
}

const STATUS_MESSAGES: Record<string, { title: string; description: string }> =
  {
    pending: {
      title: 'Queuing Request',
      description: 'Your character is being queued for generation...',
    },
    creating_pod: {
      title: 'Securing GPU Server',
      description: 'Finding an available GPU server for your generation...',
    },
    retrying: {
      title: 'High Demand',
      description:
        'GPU servers are busy. Automatically retrying with different options...',
    },
    waiting_for_pod: {
      title: 'Starting Server',
      description:
        'Your GPU server is starting up. This may take 1-2 minutes...',
    },
    loading_models: {
      title: 'Loading AI Models',
      description: 'Loading the image generation models. Almost ready...',
    },
    generating: {
      title: 'Creating Your Character',
      description:
        'AI is generating your character image. This takes about 30 seconds...',
    },
    completed: {
      title: 'Generation Complete!',
      description: 'Your character is ready. Do you approve this image?',
    },
    failed: {
      title: 'Generation Failed',
      description: 'Something went wrong. Please try again.',
    },
  };

export function GenerationModal({
  isOpen,
  generationId,
  characterName,
  onClose,
  onRetry,
}: GenerationModalProps) {
  const router = useRouter();
  const invalidateCoinBalance = useInvalidateCoinBalance();
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lock body scroll when modal is open (prevents mobile scroll hiding the fixed overlay)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Poll for status updates
  const pollStatus = useCallback(async () => {
    if (!generationId) return;

    try {
      const result = await checkGenerationStatus(generationId);
      setStatus(result);

      if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      console.error('Failed to check status:', err);
    }
  }, [generationId]);

  useEffect(() => {
    if (!isOpen || !generationId) return;

    // Initial poll
    pollStatus();

    // Continue polling until completed/failed
    const interval = setInterval(() => {
      if (
        status?.status === 'completed' ||
        status?.status === 'failed' ||
        status?.status === 'approved' ||
        status?.status === 'rejected'
      ) {
        return;
      }
      pollStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, generationId, pollStatus, status?.status]);

  // Invalidate coin balance when generation completes (coins were deducted server-side)
  useEffect(() => {
    if (status?.status === 'completed' || status?.status === 'failed') {
      invalidateCoinBalance();
    }
  }, [status?.status, invalidateCoinBalance]);

  const handleApprove = async () => {
    if (!generationId) return;

    setIsApproving(true);
    setError(null);

    try {
      const result = await approveCharacterGeneration(generationId);

      if (result.success && result.characterSlug) {
        invalidateCoinBalance();
        router.push(`/chat/${result.characterSlug}`);
      } else {
        setError(result.error || 'Failed to approve character');
      }
    } catch (err) {
      console.error('Approval failed:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!generationId) return;

    setIsRejecting(true);
    setError(null);

    try {
      await rejectCharacterGeneration(generationId);
      onClose();
    } catch (err) {
      console.error('Rejection failed:', err);
      setError('Failed to reject generation');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRetry = () => {
    setStatus(null);
    setError(null);
    onRetry();
  };

  const handleCancel = async () => {
    if (!generationId || isCancelling) return;

    setIsCancelling(true);
    setError(null);

    try {
      const result = await cancelGeneration(generationId);

      if (result.success) {
        // Invalidate coin balance since coins were refunded
        invalidateCoinBalance();
        onClose();
      } else {
        setError(result.error || 'Failed to cancel generation');
        setIsCancelling(false);
      }
    } catch (err) {
      console.error('Cancel failed:', err);
      setError('Failed to cancel generation');
      setIsCancelling(false);
    }
  };

  const currentStatus = status?.status || 'pending';
  // Human-friendly bar: time-driven, slows near the end, never parks on a
  // single number the way raw status milestones did.
  const smoothPct = useSmoothProgress({
    active: status != null,
    done: currentStatus === 'completed',
    expectedSeconds: 45,
    floor: status?.progress ?? 0,
  });
  const statusInfo = STATUS_MESSAGES[currentStatus] ?? STATUS_MESSAGES.pending!;
  const isLoading = [
    'pending',
    'creating_pod',
    'retrying',
    'waiting_for_pod',
    'loading_models',
    'generating',
  ].includes(currentStatus);
  const isCompleted = currentStatus === 'completed';
  const isFailed = currentStatus === 'failed';
  const isRetrying =
    currentStatus === 'retrying' ||
    (currentStatus === 'creating_pod' && (status?.retryAttempt ?? 0) > 1);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:items-center"
          onClick={(e) => {
            // Only handle backdrop clicks
            if (e.target === e.currentTarget) {
              if (isLoading) {
                // Cancel generation when clicking backdrop during loading
                handleCancel();
              } else {
                onClose();
              }
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative my-auto w-full max-w-sm overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 shadow-2xl sm:max-w-lg"
          >
            {/* Close button - only show when not loading */}
            {!isLoading && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 text-gray-400 transition-colors hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            )}

            {/* Content */}
            <div className="p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-6">
              {/* Character Name */}
              <div className="mb-2 text-center sm:mb-6">
                <h2 className="text-sm font-bold text-white sm:text-xl">
                  {characterName}
                </h2>
              </div>

              {/* Image Preview Area */}
              <div
                className={`relative mx-auto mb-2 aspect-[3/4] w-full max-w-[180px] overflow-hidden rounded-xl sm:mb-6 sm:max-w-xs bg-gray-800${isCompleted ? 'ring-1 ring-white/10' : ''}`}
              >
                {isCompleted && status?.finalImageUrl ? (
                  // Final image - retries on cold-CDN misses
                  <GeneratedImage
                    src={status.finalImageUrl}
                    alt={characterName}
                  />
                ) : isLoading ? (
                  // Loading state with blur effect
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                    {/* Animated background */}
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="absolute top-1/4 left-1/4 h-32 w-32 animate-pulse rounded-full bg-pink-600/20 blur-3xl" />
                      <div className="absolute right-1/4 bottom-1/4 h-40 w-40 animate-pulse rounded-full bg-orange-600/20 blur-3xl delay-300" />
                      <div className="absolute top-1/2 left-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-purple-600/20 blur-2xl delay-150" />
                    </div>

                    {/* Spinner */}
                    <div className="relative z-10 mb-2 sm:mb-4">
                      <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-700 border-t-pink-500 sm:h-16 sm:w-16" />
                    </div>

                    {/* Status text */}
                    <div className="relative z-10 px-3 text-center sm:px-4">
                      <p className="mb-0.5 text-xs font-semibold text-white sm:mb-1 sm:text-base">
                        {statusInfo.title}
                      </p>
                      <p className="text-[10px] leading-tight text-gray-400 sm:text-sm">
                        {statusInfo.description}
                      </p>
                    </div>

                    {/* Progress bar */}
                    {!isFailed && (
                      <div className="relative z-10 mt-2 w-3/4 sm:mt-4">
                        <div className="h-1.5 overflow-hidden rounded-full bg-gray-700 sm:h-2">
                          <motion.div
                            className="h-full bg-gradient-to-r from-pink-500 to-orange-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${smoothPct}%` }}
                            transition={{ duration: 0.4 }}
                          />
                        </div>
                        <p className="mt-0.5 text-center text-[10px] text-gray-500 sm:mt-1 sm:text-xs">
                          {smoothPct}%
                        </p>
                      </div>
                    )}

                    {/* Retry/High demand notice */}
                    {isRetrying && (
                      <div className="relative z-10 mt-2 space-y-1 sm:mt-4 sm:space-y-2">
                        <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-orange-400 sm:gap-2 sm:text-sm">
                          <Zap className="h-3 w-3 animate-pulse sm:h-4 sm:w-4" />
                          <span>GPU Servers Busy</span>
                        </div>
                        <div className="text-center">
                          <span className="text-[10px] text-gray-400 sm:text-xs">
                            Attempt {status?.retryAttempt ?? 1} of{' '}
                            {status?.maxRetryAttempts ?? 25}
                          </span>
                          <div className="mx-auto mt-1 h-1 w-24 overflow-hidden rounded-full bg-gray-700 sm:w-32">
                            <motion.div
                              className="h-full bg-orange-500"
                              initial={{ width: 0 }}
                              animate={{
                                width: `${Math.min(((status?.retryAttempt ?? 1) / (status?.maxRetryAttempts ?? 25)) * 100, 100)}%`,
                              }}
                              transition={{ duration: 0.3 }}
                            />
                          </div>
                        </div>
                        <p className="text-center text-[10px] text-gray-500 sm:text-xs">
                          Trying different GPU types automatically
                        </p>
                      </div>
                    )}

                    {/* Standard high volume notice (not retrying yet) */}
                    {!isRetrying &&
                      (currentStatus === 'creating_pod' ||
                        currentStatus === 'waiting_for_pod') && (
                        <div className="relative z-10 mt-2 flex items-center gap-1.5 px-2 text-center text-[10px] text-orange-400 sm:mt-4 sm:gap-2 sm:text-xs">
                          <Users className="h-3 w-3 flex-shrink-0 sm:h-4 sm:w-4" />
                          <span>
                            High demand - your position in queue is being
                            processed
                          </span>
                        </div>
                      )}
                  </div>
                ) : isFailed ? (
                  // Failed state
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
                    <AlertCircle className="mb-3 h-12 w-12 text-red-500 sm:mb-4 sm:h-16 sm:w-16" />
                    <p className="mb-1 text-sm font-semibold text-white sm:text-base">
                      Generation Failed
                    </p>
                    <p className="max-w-xs px-4 text-center text-xs text-gray-400 sm:text-sm">
                      {error?.includes('GPU servers are busy') ||
                      error?.includes('SUPPLY_CONSTRAINT')
                        ? 'All GPU servers are currently in use. Please wait a few minutes and try again.'
                        : error || 'Something went wrong. Please try again.'}
                    </p>
                    {(error?.includes('GPU servers are busy') ||
                      error?.includes('SUPPLY_CONSTRAINT')) && (
                      <p className="mt-2 text-xs text-orange-400">
                        Tip: Try again in a few minutes when servers become
                        available
                      </p>
                    )}
                  </div>
                ) : (
                  // Placeholder
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="h-12 w-12 text-gray-600" />
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && !isFailed && (
                <div className="mb-3 rounded-lg border border-red-500/50 bg-red-500/20 p-2 text-center text-xs text-red-300 sm:mb-4 sm:p-3 sm:text-sm">
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col items-stretch justify-center gap-2 sm:flex-row sm:items-center sm:gap-3">
                {isLoading && (
                  <button
                    onClick={handleCancel}
                    disabled={isCancelling}
                    className="flex items-center gap-1.5 rounded-lg bg-gray-800 px-3 py-1.5 text-xs text-gray-300 transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel'
                    )}
                  </button>
                )}

                {isCompleted && (
                  <>
                    <button
                      onClick={handleApprove}
                      disabled={isApproving || isRejecting}
                      className="order-first flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:from-pink-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-50 sm:order-last sm:w-auto sm:gap-2 sm:rounded-lg sm:px-6 sm:py-3 sm:text-sm"
                    >
                      {isApproving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Approve & Chat
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={isRejecting || isApproving}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gray-800 px-4 py-2.5 text-sm text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:gap-2 sm:rounded-lg sm:px-6 sm:py-3 sm:text-sm"
                    >
                      {isRejecting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                      Reject
                    </button>
                  </>
                )}

                {isFailed && (
                  <>
                    <button
                      onClick={handleRetry}
                      className="order-first flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:from-pink-500 hover:to-orange-400 sm:order-last sm:w-auto sm:gap-2 sm:rounded-lg sm:px-6 sm:py-3"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Try Again
                    </button>
                    <button
                      onClick={onClose}
                      className="w-full rounded-xl bg-gray-800 px-4 py-2.5 text-sm text-white transition-colors hover:bg-gray-700 sm:w-auto sm:rounded-lg sm:px-6 sm:py-3"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
